import { router } from '@inertiajs/react'
import React, { useCallback, useEffect, useRef } from 'react'
import { KitchenHead } from './Components/KitchenHead'
import { KitchenHeader } from './Components/KitchenHeader'
import { LockScreen } from './Components/LockScreen'
import { OrderCard } from './Components/OrderCard'
import { useAudioNotifications } from './hooks/useAudioNotifications'
import { useInactivityLock } from './hooks/useInactivityLock'
import { useKitchenChannel } from './hooks/useKitchenChannel'

const STATUS_BADGES = {
  paid: { label: 'New', className: 'bg-red-100 text-red-800' },
  confirmed: { label: 'New', className: 'bg-red-100 text-red-800' },
  preparing: { label: 'Preparing', className: 'bg-yellow-100 text-yellow-800' },
  ready: { label: 'Ready', className: 'bg-green-100 text-green-800' },
  collected: { label: 'Completed', className: 'bg-gray-200 text-gray-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500 line-through' }
}

export default function Today({ account, accounts, orders }) {
  const ordersRef = useRef(orders)

  useEffect(() => {
    ordersRef.current = orders
  }, [orders])

  const pending = orders.filter(o => o.status === 'paid' || o.status === 'confirmed')
  const preparing = orders.filter(o => o.status === 'preparing')
  const ready = orders.filter(o => o.status === 'ready')

  const getUnactionedOrders = useCallback(
    () => ordersRef.current.filter(o => o.status === 'paid' || o.status === 'confirmed'),
    []
  )

  const { audioEnabled, enableAudio, disableAudio } = useAudioNotifications({
    account,
    orders,
    getUnactionedOrders
  })

  const {
    isLocked,
    setIsLocked,
    unlockInput,
    shakeError,
    handleUnlockDigit,
    handleUnlockClear,
    handleUnlockBackspace,
    handleScreenTouch
  } = useInactivityLock({ unlockCode: account.kitchen_unlock_code })

  // Keep the list fresh the same way the live board does
  const reload = useCallback(() => router.reload({ only: ['orders'] }), [])

  useKitchenChannel({ accountId: account.id, onOrdersChanged: reload })

  useEffect(() => {
    const interval = setInterval(reload, 15000)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') reload()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', reload)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', reload)
    }
  }, [reload])

  const activeCount = orders.filter(o => !['collected', 'cancelled'].includes(o.status)).length
  const completedCount = orders.filter(o => o.status === 'collected').length

  return (
    <div className="min-h-screen bg-gray-100">
      <KitchenHead
        account={account}
        newCount={pending.length}
        preparingCount={preparing.length}
        readyCount={ready.length}
      />

      {isLocked && (
        <LockScreen
          pending={pending.length}
          preparing={preparing.length}
          ready={ready.length}
          audioEnabled={audioEnabled}
          unlockCode={account.kitchen_unlock_code}
          unlockInput={unlockInput}
          shakeError={shakeError}
          onDigit={handleUnlockDigit}
          onClear={handleUnlockClear}
          onBackspace={handleUnlockBackspace}
          onScreenTouch={handleScreenTouch}
        />
      )}

      <KitchenHeader
        account={account}
        accounts={accounts}
        view="today"
        audioEnabled={audioEnabled}
        onToggleAudio={() => audioEnabled ? disableAudio() : enableAudio()}
        onLock={() => setIsLocked(true)}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">All orders placed today</h2>
            <p className="text-sm text-gray-600">
              Read-only view — includes completed and cancelled orders that are no longer on the live board.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {orders.length} total · {activeCount} active · {completedCount} completed
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
            No orders have been placed today.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.map(order => {
              const badge = STATUS_BADGES[order.status] || { label: order.status, className: 'bg-gray-100 text-gray-700' }
              return (
                <div key={order.id} className={order.status === 'cancelled' ? 'opacity-60' : ''}>
                  <div className="flex items-center justify-between mb-1 px-1">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide ${badge.className}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(order.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <OrderCard order={order} showActions={false} />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
